import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Baby, 
  Calendar, 
  Activity, 
  FileText, 
  Phone, 
  Mail, 
  MapPin,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  Users
} from 'lucide-react';
import { patients } from '../services/api';

const ParentChildDetails = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchChildDetails();
  }, [childId]);

  const fetchChildDetails = async () => {
    try {
      setLoading(true);
      
      // Get child details
      const response = await patients.getById(childId);
      setChild(response.data.data);

    } catch (error) {
      console.error('Error fetching child details:', error);
      setError('Falha ao carregar detalhes da criança');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleViewSessions = () => {
    navigate(`/parent/children/${childId}/sessions`);
  };

  const handleViewBehaviors = () => {
    navigate(`/parent/children/${childId}/behaviors`);
  };

  const handleViewTreatmentPlan = () => {
    navigate(`/parent/children/${childId}/treatment-plan`);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-800 border border-red-200 rounded-lg p-4">
          {error}
        </div>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-lg p-4">
          Criança não encontrada
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/parent/dashboard')}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {child.firstName} {child.lastName}
            </h1>
            <p className="text-gray-600">Detalhes da criança</p>
          </div>
        </div>
      </div>

      {/* Child Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Baby className="w-5 h-5" />
            Informações da Criança
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Nome Completo</h3>
              <p className="text-lg text-gray-900">{child.firstName} {child.lastName}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">ID do Paciente</h3>
              <p className="text-sm text-gray-600 font-mono">{child.patientId}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Data de Nascimento</h3>
              <p className="text-lg text-gray-900 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                {child.dateOfBirth 
                  ? new Date(child.dateOfBirth).toLocaleDateString('pt-BR')
                  : 'Não informado'
                }
                {child.dateOfBirth && (
                  <span className="text-sm text-gray-500">
                    ({calculateAge(child.dateOfBirth)} anos)
                  </span>
                )}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Gênero</h3>
              <p className="text-lg text-gray-900">{child.gender || 'Não informado'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Diagnóstico Principal</h3>
              <p className="text-lg text-gray-900">{child.primaryDiagnosis || 'Não informado'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Status</h3>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                child.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {child.status === 'active' ? 'Ativo' : child.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Ações Rápidas</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={handleViewSessions}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-brand-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Sessões</h3>
                  <p className="text-sm text-gray-600">Ver histórico de sessões</p>
                </div>
              </div>
            </button>

            <button
              onClick={handleViewBehaviors}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Activity className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Comportamentos</h3>
                  <p className="text-sm text-gray-600">Acompanhar comportamentos</p>
                </div>
              </div>
            </button>

            <button
              onClick={handleViewTreatmentPlan}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Plano de Tratamento</h3>
                  <p className="text-sm text-gray-600">Ver plano atual</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Progress Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Visão Geral do Progresso
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-brand-50 rounded-lg">
              <div className="text-2xl font-bold text-brand-600">0</div>
              <div className="text-sm text-gray-600">Sessões Realizadas</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">0</div>
              <div className="text-sm text-gray-600">Comportamentos Registrados</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">0</div>
              <div className="text-sm text-gray-600">Objetivos Alcançados</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">-</div>
              <div className="text-sm text-gray-600">Progresso Geral</div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Informações Adicionais</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Data de Início</h3>
              <p className="text-lg text-gray-900">
                {child.startDate 
                  ? new Date(child.startDate).toLocaleDateString('pt-BR')
                  : 'Não informado'
                }
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Diagnósticos Secundários</h3>
              <p className="text-lg text-gray-900">
                {child.secondaryDiagnoses || 'Nenhum'}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Alergias</h3>
              <p className="text-lg text-gray-900">
                {child.allergies || 'Nenhuma'}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Medicamentos</h3>
              <p className="text-lg text-gray-900">
                {child.medications || 'Nenhum'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentChildDetails; 